import { useEffect, useRef, useState } from 'react';
import { useGameStore } from '../../../store/gameStore';
import { ScaleToFit } from '../ScaleToFit';
import { useGameLoop } from '../../../hooks/useGameLoop';
import { useKeyboard } from '../../../hooks/useKeyboard';
import { playBrick, playPaddle, playGolden, playBallOut, playPowerUp } from '../../../utils/sounds';

const W = 580;
const H = 420;
const BALL_R = 7;
const PADDLE_W = 90;
const PADDLE_H = 12;
const PADDLE_Y = H - 30;
const PADDLE_SPEED = 6;
const BRICK_COLS = 8;
const BRICK_ROWS = 5;
const BRICK_W = 58;
const BRICK_H = 20;
const BRICK_GAP = 4;
const OFFSET_X = (W - (BRICK_COLS * (BRICK_W + BRICK_GAP) - BRICK_GAP)) / 2;
const OFFSET_Y = 50;
const BRICK_COLORS = ['#EF4444', '#F97316', '#EAB308', '#22C55E', '#3B82F6'];
const MAX_BALL_SPEED = 12;
const CAPSULE_SPEED = 2;
const WIDE_DURATION = 8000;
const SLOW_DURATION = 6000;
const SLOW_FACTOR = 0.65;

type PowerUpType = 'wide' | 'slow' | 'life';

const PU_COLORS: Record<PowerUpType, string> = {
  wide: '#A855F7',
  slow: '#06B6D4',
  life: '#22C55E',
};
const PU_LABELS: Record<PowerUpType, string> = { wide: 'W', slow: 'S', life: '+' };
const PU_DISPLAY: Record<PowerUpType, string> = {
  wide: 'WIDE PADDLE',
  slow: 'SLOW BALL',
  life: '',
};

const PU_BRICKS: Array<{ r: number; c: number; type: PowerUpType }> = [
  { r: 1, c: 5, type: 'wide' },
  { r: 3, c: 2, type: 'slow' },
  { r: 0, c: 6, type: 'life' },
];

interface Brick {
  x: number;
  y: number;
  broken: boolean;
  gold: boolean;
  powerUp: PowerUpType | null;
  color: string;
}

interface Capsule {
  id: number;
  x: number;
  y: number;
  type: PowerUpType;
}

function initBricks(): Brick[] {
  const puMap = new Map(PU_BRICKS.map(({ r, c, type }) => [`${r},${c}`, type]));
  const bricks: Brick[] = [];
  for (let r = 0; r < BRICK_ROWS; r++) {
    for (let c = 0; c < BRICK_COLS; c++) {
      const isGold = r === 2 && c === 3;
      const powerUp: PowerUpType | null = isGold ? null : (puMap.get(`${r},${c}`) ?? null);
      const color = powerUp
        ? PU_COLORS[powerUp]
        : (BRICK_COLORS[r % BRICK_COLORS.length] ?? '#3B82F6');
      bricks.push({
        x: OFFSET_X + c * (BRICK_W + BRICK_GAP),
        y: OFFSET_Y + r * (BRICK_H + BRICK_GAP),
        broken: false,
        gold: isGold,
        powerUp,
        color,
      });
    }
  }
  return bricks;
}

interface Props {
  active: boolean;
}

export function BreakoutGame({ active }: Props) {
  const diveIn = useGameStore((s) => s.diveIn);
  const addScore = useGameStore((s) => s.addScore);
  const gameOver = useGameStore((s) => s.gameOver);
  const phase = useGameStore((s) => s.phase);
  const runCount = useGameStore((s) => s.runCount);
  const keys = useKeyboard();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const speedBoost = Math.min(2, Math.floor((runCount - 1) / 2) * 0.5);
  const ballRef = useRef({ x: W / 2, y: H - 80, vx: 3 + speedBoost * 0.5, vy: -(5 + speedBoost) });
  const paddleXRef = useRef((W - PADDLE_W) / 2);
  const bricksRef = useRef<Brick[]>(initBricks());
  const livesRef = useRef(3);
  const divingRef = useRef(false);
  const endedRef = useRef(false);

  // Power-up state
  const capsulesRef = useRef<Capsule[]>([]);
  const capsuleIdRef = useRef(0);
  const paddleWRef = useRef(PADDLE_W);
  const slowRef = useRef(false);
  const wideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const slowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [lives, setLives] = useState(3);
  const [effects, setEffects] = useState<Set<PowerUpType>>(new Set());

  useEffect(() => {
    if (active) divingRef.current = false;
  }, [active]);

  useEffect(() => {
    return () => {
      if (wideTimerRef.current) clearTimeout(wideTimerRef.current);
      if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
    };
  }, []);

  function applyPowerUp(type: PowerUpType) {
    playPowerUp();
    if (type === 'wide') {
      if (wideTimerRef.current) clearTimeout(wideTimerRef.current);
      paddleWRef.current = PADDLE_W + 40;
      setEffects((e) => new Set([...e, 'wide']));
      wideTimerRef.current = setTimeout(() => {
        paddleWRef.current = PADDLE_W;
        setEffects((e) => {
          const n = new Set(e);
          n.delete('wide');
          return n;
        });
        wideTimerRef.current = null;
      }, WIDE_DURATION);
    } else if (type === 'slow') {
      if (!slowRef.current) {
        slowRef.current = true;
        const b = ballRef.current;
        b.vx *= SLOW_FACTOR;
        b.vy *= SLOW_FACTOR;
        setEffects((e) => new Set([...e, 'slow']));
        if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
        slowTimerRef.current = setTimeout(() => {
          if (slowRef.current) {
            slowRef.current = false;
            const b = ballRef.current;
            b.vx /= SLOW_FACTOR;
            b.vy /= SLOW_FACTOR;
          }
          setEffects((e) => {
            const n = new Set(e);
            n.delete('slow');
            return n;
          });
          slowTimerRef.current = null;
        }, SLOW_DURATION);
      }
    } else if (type === 'life') {
      if (livesRef.current < 5) {
        livesRef.current += 1;
        setLives(livesRef.current);
      }
    }
  }

  function drawFrame(ctx: CanvasRenderingContext2D) {
    ctx.fillStyle = '#111827';
    ctx.fillRect(0, 0, W, H);

    // Bricks
    for (const b of bricksRef.current) {
      if (b.broken) continue;
      if (b.gold) {
        ctx.fillStyle = '#FBBF24';
        ctx.fillRect(b.x, b.y, BRICK_W - 2, BRICK_H - 2);
        ctx.strokeStyle = '#F59E0B';
        ctx.lineWidth = 2;
        ctx.strokeRect(b.x + 1, b.y + 1, BRICK_W - 4, BRICK_H - 4);
        ctx.fillStyle = '#FEF08A';
        ctx.font = 'bold 12px monospace';
        ctx.textAlign = 'center';
        ctx.fillText('★', b.x + BRICK_W / 2 - 1, b.y + BRICK_H / 2 + 4);
      } else if (b.powerUp) {
        ctx.fillStyle = b.color;
        ctx.fillRect(b.x, b.y, BRICK_W - 2, BRICK_H - 2);
        ctx.strokeStyle = 'rgba(255,255,255,0.45)';
        ctx.lineWidth = 1;
        ctx.strokeRect(b.x + 1, b.y + 1, BRICK_W - 4, BRICK_H - 4);
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 11px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(PU_LABELS[b.powerUp], b.x + BRICK_W / 2 - 1, b.y + BRICK_H / 2 + 4);
      } else {
        ctx.fillStyle = b.color;
        ctx.fillRect(b.x, b.y, BRICK_W - 2, BRICK_H - 2);
      }
    }

    // Falling power-up capsules
    for (const cap of capsulesRef.current) {
      ctx.fillStyle = PU_COLORS[cap.type];
      ctx.fillRect(cap.x - 12, cap.y - 7, 24, 14);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 10px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(PU_LABELS[cap.type], cap.x, cap.y + 4);
    }

    // Paddle (purple tint when wide)
    ctx.fillStyle = paddleWRef.current > PADDLE_W ? '#C084FC' : '#60A5FA';
    ctx.fillRect(paddleXRef.current, PADDLE_Y, paddleWRef.current, PADDLE_H);

    // Ball (blue tint when slowed)
    const ball = ballRef.current;
    ctx.fillStyle = slowRef.current ? '#7DD3FC' : '#F9FAFB';
    ctx.beginPath();
    ctx.arc(ball.x, ball.y, BALL_R, 0, Math.PI * 2);
    ctx.fill();

    // Lives dots (bottom-left)
    for (let i = 0; i < livesRef.current; i++) {
      ctx.fillStyle = '#EF4444';
      ctx.beginPath();
      ctx.arc(12 + i * 20, H - 10, 6, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = '#4B5563';
    ctx.font = '12px monospace';
    ctx.textAlign = 'right';
    ctx.fillText('Break ★ to dive!', W - 8, H - 6);
  }

  useGameLoop(
    () => {
      if (endedRef.current || divingRef.current) return;
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;

      // Move paddle
      if (keys.has('ArrowLeft') || keys.has('KeyA')) {
        paddleXRef.current = Math.max(0, paddleXRef.current - PADDLE_SPEED);
      }
      if (keys.has('ArrowRight') || keys.has('KeyD')) {
        paddleXRef.current = Math.min(W - paddleWRef.current, paddleXRef.current + PADDLE_SPEED);
      }

      // Move capsules and check paddle catch
      capsulesRef.current = capsulesRef.current.filter((cap) => {
        cap.y += CAPSULE_SPEED;
        const pw = paddleWRef.current;
        const px = paddleXRef.current;
        if (
          cap.y + 7 >= PADDLE_Y &&
          cap.y - 7 <= PADDLE_Y + PADDLE_H &&
          cap.x >= px - 12 &&
          cap.x <= px + pw + 12
        ) {
          applyPowerUp(cap.type);
          return false;
        }
        return cap.y < H + 20;
      });

      const ball = ballRef.current;
      ball.x += ball.vx;
      ball.y += ball.vy;

      // Wall bounces
      if (ball.x - BALL_R <= 0) { ball.vx = Math.abs(ball.vx); ball.x = BALL_R; }
      if (ball.x + BALL_R >= W) { ball.vx = -Math.abs(ball.vx); ball.x = W - BALL_R; }
      if (ball.y - BALL_R <= 0) { ball.vy = Math.abs(ball.vy); ball.y = BALL_R; }

      // Paddle bounce
      const px = paddleXRef.current;
      const pw = paddleWRef.current;
      if (
        ball.vy > 0 &&
        ball.y + BALL_R >= PADDLE_Y &&
        ball.y + BALL_R <= PADDLE_Y + PADDLE_H + 2 &&
        ball.x >= px &&
        ball.x <= px + pw
      ) {
        const offset = (ball.x - px) / pw - 0.5;
        ball.vx = offset * 10;
        ball.vy = -Math.abs(ball.vy) * 1.04;
        const speed = Math.sqrt(ball.vx ** 2 + ball.vy ** 2);
        if (speed > MAX_BALL_SPEED) {
          ball.vx = (ball.vx / speed) * MAX_BALL_SPEED;
          ball.vy = (ball.vy / speed) * MAX_BALL_SPEED;
        }
        ball.y = PADDLE_Y - BALL_R - 1;
        playPaddle();
      }

      // Ball out
      if (ball.y - BALL_R > H) {
        playBallOut();
        livesRef.current -= 1;
        setLives(livesRef.current);
        if (livesRef.current <= 0) {
          endedRef.current = true;
          gameOver();
          return;
        }
        const baseVx = 3 + speedBoost * 0.5;
        const baseVy = -(5 + speedBoost);
        ball.x = W / 2;
        ball.y = H - 80;
        ball.vx = slowRef.current ? baseVx * SLOW_FACTOR : baseVx;
        ball.vy = slowRef.current ? baseVy * SLOW_FACTOR : baseVy;
      }

      // Brick collisions
      for (const b of bricksRef.current) {
        if (b.broken) continue;
        if (
          ball.x + BALL_R > b.x &&
          ball.x - BALL_R < b.x + BRICK_W &&
          ball.y + BALL_R > b.y &&
          ball.y - BALL_R < b.y + BRICK_H
        ) {
          b.broken = true;
          const overlapL = ball.x + BALL_R - b.x;
          const overlapR = b.x + BRICK_W - (ball.x - BALL_R);
          const overlapT = ball.y + BALL_R - b.y;
          const overlapB = b.y + BRICK_H - (ball.y - BALL_R);
          const minH = Math.min(overlapL, overlapR);
          const minV = Math.min(overlapT, overlapB);
          if (minH < minV) ball.vx *= -1;
          else ball.vy *= -1;

          const spd = Math.sqrt(ball.vx ** 2 + ball.vy ** 2);
          const newSpd = Math.min(spd * 1.02, MAX_BALL_SPEED);
          if (spd > 0) {
            ball.vx = (ball.vx / spd) * newSpd;
            ball.vy = (ball.vy / spd) * newSpd;
          }

          if (b.gold) {
            addScore(50);
            divingRef.current = true;
            playGolden();
            diveIn();
          } else if (b.powerUp) {
            addScore(15);
            capsulesRef.current.push({
              id: capsuleIdRef.current++,
              x: b.x + BRICK_W / 2,
              y: b.y + BRICK_H,
              type: b.powerUp,
            });
            playBrick();
          } else {
            addScore(5);
            playBrick();
          }
          break;
        }
      }

      drawFrame(ctx);
    },
    active && phase === 'playing',
  );

  return (
    <div className="flex flex-col items-center gap-3 p-4">
      <div className="text-xs text-gray-400 font-mono">
        Lives: {lives} &nbsp;|&nbsp; Break the{' '}
        <span className="text-yellow-400">★ golden brick</span> to dive deeper
      </div>
      {effects.size > 0 && (
        <div className="flex gap-2">
          {[...effects]
            .filter((e) => PU_DISPLAY[e])
            .map((e) => (
              <span
                key={e}
                className="text-xs font-mono px-2 py-0.5 rounded animate-pulse"
                style={{ background: PU_COLORS[e], color: '#fff' }}
              >
                {PU_DISPLAY[e]}
              </span>
            ))}
        </div>
      )}
      <ScaleToFit nativeWidth={W} nativeHeight={H}>
        <canvas
          ref={canvasRef}
          width={W}
          height={H}
          className="border border-gray-700 rounded"
        />
      </ScaleToFit>
    </div>
  );
}
